import {useFluxStore} from 'client/framework';
import React, {useEffect, useState, useRef, useCallback, useMemo} from 'react';
import {Transforms} from 'slate';
import {useSlateStatic, ReactEditor, useSelected, useFocused} from 'slate-react';
import classnames from 'classnames';
import {debounce} from 'lodash';
import sessionStore from 'client/Session/SessionStore';

import {
  Banner,
  Button,
  Dialogue,
  Icon,
  IconButton,
  Modal,
  Text,
  addGenericErrorToast
} from '@albert-io/atomic';
import notifier from '@albert-io/notifier';
import useOnClickOutside from 'lib/hooks/useOnClickOutside';

import WrittenSubmissionContext from '../../WrittenSubmission.context';
import {deleteMedia, deleteGuessMedia, getMedia} from '../../../FreeResponseQuestion.queries';
import type {WSStore} from '../../../FreeResponseQuestion.types';

import {handleRotation, getMediaUrl} from '../Utils/editor.utils';
import type {DeletedImageElement, ImageElement} from '../slate.types';

import type {ElementProps} from './Elements.types';

interface DeletedProps extends ElementProps {}

export const DeletedImage = ({attributes, children}: DeletedProps) => {
  return (
    <div {...attributes}>
      <Banner>
        <Banner.Icon icon='image' />
        Image removed by instructor.
        {children}
      </Banner>
    </div>
  );
};

interface ImageProps extends ElementProps {
  element: ImageElement;
}

const Image = ({attributes, children, element, readOnly}: ImageProps) => {
  const isStudent = useFluxStore(sessionStore, (s) => s.isStudent());
  const editor = useSlateStatic();
  const path = ReactEditor.findPath(editor, element);
  const focused = useFocused();
  const selected = useSelected();
  const {rotation} = element;
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteErrModal, setShowDeleteErrModal] = useState(false);
  const [imageChecked, setImageChecked] = useState(false);
  const [width, setWidth] = useState(0);
  const [showPopover, setShowPopover] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const isMountedRef = useRef(true);

  useOnClickOutside(
    containerRef,
    useCallback(() => setShowPopover(false), [setShowPopover])
  );

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    // This is a workaround that checks if image was deleted
    const checkImage = async (id: string) => {
      try {
        await getMedia(id);
      } catch (error) {
        if (!isMountedRef.current) {
          return;
        }
        const code = (error as any).status;
        if (code === 404) {
          // Re-resolve the current path; the captured `path` may be stale
          // after the await if sibling nodes shifted.
          const currentPath = ReactEditor.findPath(editor, element);
          Transforms.removeNodes(editor, {at: currentPath});
          const Node: DeletedImageElement = {type: 'deleted-image', children: [{text: ''}]};
          Transforms.insertNodes(editor, Node, {at: currentPath});
        } else {
          notifier.notify(error as Error, {
            message: 'Failed to verify free response image media',
            tags: 'free-response'
          });
          addGenericErrorToast();
        }
      }
    };
    if (!imageChecked) {
      checkImage(element.id);
      // oxlint-disable-next-line react/react-compiler -- one-time existence check against the external supplement API
      setImageChecked(true);
    }
  }, [element.id, editor, path, imageChecked, element]);

  const imageClasses = classnames('written-submission__image', {
    'written-submission__image-focused': (focused && selected) || showPopover,
    rotate: !!rotation
  });

  const containerClass = classnames('written-submission__image-container', {
    rotate: !!rotation
  });

  const actionsBarClass = classnames('written-submission__image-popup', {
    invisible: !(focused && selected) && !showPopover && !readOnly
  });

  const getSize = useCallback(() => {
    if (imageRef.current) {
      setWidth(imageRef.current.offsetWidth);
    }
  }, [imageRef]);

  // oxlint-disable-next-line react/react-compiler -- the ref is only read when the debounced/stable callback fires later, not during render
  const debouncedGetSize = useMemo(() => debounce(() => getSize(), 500), [getSize]);

  useEffect(() => {
    global.addEventListener('resize', debouncedGetSize);
    return () => {
      debouncedGetSize.cancel();
      global.removeEventListener('resize', debouncedGetSize);
    };
  }, [debouncedGetSize]);

  const handleDelete = async (id, store: WSStore | null) => {
    if (store === null) {
      return;
    }
    try {
      await deleteMedia(id, store.getDraftId());
      Transforms.removeNodes(editor, {at: path});
    } catch (err) {
      notifier.notify(err as Error, {
        message: 'Failed to delete free response image media',
        tags: 'free-response'
      });
      setShowDeleteErrModal(true);
    }
  };

  const deleteFromGuess = async (id, store: WSStore | null) => {
    if (store === null) {
      return;
    }
    try {
      await deleteGuessMedia(id, store.getGuessId());
      Transforms.removeNodes(editor, {at: path});
      const Node: DeletedImageElement = {type: 'deleted-image', children: [{text: ''}]};
      Transforms.insertNodes(editor, Node, {at: path});
    } catch (_err) {
      setShowDeleteErrModal(true);
    }
  };

  const style = rotation === 1 ? {width: '100%', height: width} : undefined;
  return (
    <WrittenSubmissionContext.Consumer>
      {({store, submitted}) => (
        <div {...attributes}>
          <div contentEditable={false} className={containerClass} style={style} ref={containerRef}>
            <img
              ref={imageRef}
              className={imageClasses}
              src={getMediaUrl(element.id)}
              alt=''
              onLoad={() => getSize()}
            />
            {children}
            <div className={actionsBarClass}>
              <IconButton
                icon={['far', 'magnifying-glass-plus']}
                label='zoom'
                onClick={() => setShowViewModal(true)}
              />
              {!submitted && (
                <IconButton
                  icon='rotate-right'
                  onClick={() => handleRotation(editor)}
                  label='rotate image'
                />
              )}
              {(!submitted || !isStudent) && (
                <IconButton
                  color='negative'
                  icon={['far', 'trash-can']}
                  onClick={() => {
                    if (!submitted) {
                      handleDelete(element.id, store);
                    } else {
                      setShowDeleteModal(true);
                    }
                  }}
                  label='delete image'
                />
              )}
            </div>
            {showDeleteModal && (
              <DeleteImageModal
                onClose={() => setShowDeleteModal(false)}
                onCancel={() => setShowDeleteModal(false)}
                onConfirm={() => deleteFromGuess(element.id, store)}
              />
            )}
            {showDeleteErrModal && (
              <ImageErrorModal deleted handleClose={() => setShowDeleteErrModal(false)} />
            )}
            {showViewModal && (
              <ViewImageModal
                url={getMediaUrl(element.id)}
                onClose={() => setShowViewModal(false)}
              />
            )}
          </div>
        </div>
      )}
    </WrittenSubmissionContext.Consumer>
  );
};

export default Image;

/* Modals */

interface ViewImageModalProps {
  onClose: any;
  url: string;
}

const ViewImageModal = ({onClose, url}: ViewImageModalProps) => {
  return (
    <Modal handleClose={onClose} ariaLabel='View Image'>
      {({CloseButtonWrapper, modalContentStyle}) => {
        return (
          <div
            className={`${modalContentStyle} rounded-x1 border-border-primary background-subtle p-x1 border border-solid`}
          >
            <img src={url} alt='' style={{width: '100%'}} />
            <CloseButtonWrapper>
              <Button color='secondary'>Close</Button>
            </CloseButtonWrapper>
          </div>
        );
      }}
    </Modal>
  );
};

interface DeleteImageModalProps {
  onConfirm: any;
  onCancel: any;
  onClose: any;
}

const DeleteImageModal = ({onClose, onConfirm, onCancel}: DeleteImageModalProps) => {
  return (
    <Modal handleClose={onClose} role='dialog' ariaLabel='Delete Image'>
      {() => {
        return (
          <Dialogue
            inModal
            size='m'
            paddingLevel={4}
            alignTitle='center'
            handleClose={onClose}
            hideCloseBtn
          >
            <Text size='xl' bold as='p'>
              Are you sure you want to delete this image?
            </Text>
            <Dialogue.Body>
              <Text color='secondary'>
                The image will be removed permanently from Albert. This action cannot be undone.
              </Text>
            </Dialogue.Body>
            <Dialogue.BtnGroup align='right'>
              <Button color='secondary' onClick={onCancel}>
                No, cancel
              </Button>
              <Button color='negative' onClick={onConfirm}>
                Yes, delete
              </Button>
            </Dialogue.BtnGroup>
          </Dialogue>
        );
      }}
    </Modal>
  );
};

interface CommonErrorProps {
  handleClose: any;
  // errors?: any[]; @TODO: add error message handling
}

type DeleteProps = {
  deleted: true;
  handleRetry?: never;
};

type UploadProps = {
  deleted?: false;
  handleRetry: any;
};

type ConditionalModalProps = DeleteProps | UploadProps;

type ImageErrorModalProps = CommonErrorProps & ConditionalModalProps;

export const ImageErrorModal = ({
  handleClose,
  handleRetry,
  deleted = false
}: ImageErrorModalProps) => {
  return (
    <Modal handleClose={handleClose} role='dialog' ariaLabel='Image error'>
      {() => {
        return (
          <Dialogue size='m' inModal hideCloseBtn paddingLevel={3} alignTitle='center'>
            <Icon className='mx-auto w-full' icon={['far', 'exclamation-triangle']} />
            <Text size='xl' bold as='p' className='my-x2 text-center'>
              {deleted ? 'Delete' : 'Upload'} Error
            </Text>
            <Dialogue.Body className='mb-x0 px-x2 text-center'>
              {deleted && (
                <Text color='secondary'>
                  An error occurred and your image was unable to be deleted.
                </Text>
              )}
              {!deleted && (
                <Text color='secondary'>
                  Images must be in JPG, GIF, or PNG formats with a maximum file size of 10mb.
                </Text>
              )}
            </Dialogue.Body>
            <Dialogue.BtnGroup align='center' direction='column' fillColumn>
              {!deleted && <Button onClick={handleRetry}>Try Again</Button>}
              <Button color='secondary' onClick={handleClose}>
                Dismiss
              </Button>
            </Dialogue.BtnGroup>
          </Dialogue>
        );
      }}
    </Modal>
  );
};
